import type { FailureReportInput, FailureReportResponse } from "@repo/shared-types/failure-reports";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type Ref, useEffect, useRef, useSyncExternalStore } from "react";
import { req } from "./client.ts";
import { dismissRequestFailure, getRequestFailure, subscribeRequestFailures } from "./request-failures.ts";
import { Button, ErrorText, Feedback } from "./ui/index.ts";

type ReportStatus = "idle" | "pending" | "error" | "success";
type FailureReportCardProps = { failure: FailureReportInput; status: ReportStatus; onReport: () => void; onDismiss: () => void; ref?: Ref<HTMLElement> };

export function FailureReportCard({ failure, status, onReport, onDismiss, ref }: FailureReportCardProps) {
  return (
    <section ref={ref} className="card stack failure-report" aria-label="Problem reporting">
      {status === "success" ? (
        <Feedback>Report sent to the caretakers in Linear. Thank you.</Feedback>
      ) : (
        <>
          <h2 className="t-title" role="status">
            Something went wrong. Help us investigate.
          </h2>
          <p className="t-body-sm measure">
            Send technical details about the failed operation, together with your name and email, to the caretakers in Linear. Form answers and attachments are excluded.
          </p>
          <details>
            <summary>Technical details included</summary>
            <dl className="t-body-sm">
              <dt>Operation</dt>
              <dd>
                {failure.method} {failure.operation}
              </dd>
              <dt>Result</dt>
              <dd>
                {failure.status ?? "No HTTP error status"}
                {failure.errorCode ? ` · ${failure.errorCode}` : ""}
              </dd>
              <dt>Time</dt>
              <dd>{failure.occurredAt}</dd>
              <dt>Duration</dt>
              <dd>{failure.durationMs} ms</dd>
              <dt>Browser</dt>
              <dd>{failure.browser}</dd>
              <dt>Connection</dt>
              <dd>{failure.online ? "Online" : "Offline"}</dd>
              <dt>Report reference</dt>
              <dd>{failure.reportId}</dd>
              {failure.correlationId && (
                <>
                  <dt>Request reference</dt>
                  <dd>{failure.correlationId}</dd>
                </>
              )}
            </dl>
          </details>
          {status === "error" && <ErrorText>Could not send the report. Check your connection and try again.</ErrorText>}
        </>
      )}
      <div className="failure-report__actions">
        {status !== "success" && (
          <Button disabled={status === "pending"} onClick={onReport}>
            {status === "pending" ? "Sending report…" : status === "error" ? "Retry report" : "Report problem"}
          </Button>
        )}
        <Button variant="ghost" disabled={status === "pending"} onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </section>
  );
}

export function FailureReportPrompt() {
  const failure = useSyncExternalStore(subscribeRequestFailures, getRequestFailure, getRequestFailure);
  const config = useQuery({ queryKey: ["failure-reports", "config"], queryFn: () => req<{ enabled: boolean }>("/api/failure-reports/config"), staleTime: 60_000 });
  const report = useMutation({ mutationFn: (input: FailureReportInput) => req<FailureReportResponse>("/api/failure-reports", { method: "POST", body: JSON.stringify(input) }) });
  const active = report.isPending ? report.variables : failure;
  const visible = !!active && !!config.data?.enabled;
  const card = useRef<HTMLElement>(null);
  // Reveal the prompt once; replacement failures must not interrupt ongoing work.
  useEffect(() => {
    if (visible) card.current?.scrollIntoView?.({ block: "start" });
  }, [visible]);
  if (!visible) return null;
  const status: ReportStatus = report.variables?.reportId === active.reportId ? report.status : "idle";
  return <FailureReportCard ref={card} failure={active} status={status} onReport={() => report.mutate(active)} onDismiss={() => dismissRequestFailure(active.reportId)} />;
}
